import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
// FolderSearchBar.tsx -- top-bar search input with folder-scoped
// semantic search. Mirrors the workflow the user had in the POC
// (~/src/raindb-app), where being "in" a folder limited search to
// that folder's documents (+ descendants).
//
// User direction (verbatim, this session):
//   "I need the functionality like I had in the POC where I can be
//    in a folder and do a search and have it only isolate the search
//    to the documents in the folder tree instead of everywhere."
//
// Design (workbench D.6):
//   - Input with placeholder describing scope ("Search this folder"
//     vs "Search project")
//   - Debounced search (300ms) so typing doesn't fire a request
//     per keystroke
//   - Dropdown of hits below the input: filename + snippet + score
//   - Click a hit to open the document preview (existing path)
//   - Empty-state: "No matches"
//   - Outside click + Escape close the dropdown
import { useCallback, useEffect, useMemo, useRef, useState, } from 'react';
import { api } from '../../utils/api.js';
const DEBOUNCE_MS = 300;
const MAX_HITS = 10;
export function FolderSearchBar({ projectId, folderId, scopeLabel, atRoot, onOpenHit, }) {
    const [input, setInput] = useState('');
    const [hits, setHits] = useState([]);
    const [loading, setLoading] = useState(false);
    const [err, setErr] = useState(null);
    const [open, setOpen] = useState(false);
    const wrapRef = useRef(null);
    const debounceRef = useRef(null);
    // requestSeq guards against out-of-order responses: a slow earlier
    // fetch could resolve after a faster later one and overwrite fresh
    // hits with stale ones. We track the latest sequence number and
    // ignore responses whose seq is older.
    const requestSeq = useRef(0);
    // Reset hits + error when the folder context changes -- a fresh
    // scope shouldn't carry over stale results from the old folder.
    useEffect(() => {
        setHits([]);
        setErr(null);
    }, [folderId]);
    const performSearch = useCallback(async (query) => {
        if (!query.trim()) {
            setHits([]);
            return;
        }
        const mySeq = ++requestSeq.current;
        setLoading(true);
        setErr(null);
        try {
            const r = await api.post(`/p/${projectId}/search/folder`, { query: query.trim(), folderId, topK: MAX_HITS });
            // Drop the response if a newer request has already started --
            // race protection (see requestSeq comment above).
            if (mySeq !== requestSeq.current)
                return;
            setHits(r.hits ?? []);
        }
        catch (e) {
            if (mySeq !== requestSeq.current)
                return;
            setErr(e instanceof Error ? e.message : 'search failed');
            setHits([]);
        }
        finally {
            if (mySeq === requestSeq.current)
                setLoading(false);
        }
    }, [projectId, folderId]);
    // Debounced search trigger: any change to `input` schedules a
    // search 300ms later, cancelling any prior pending search. Empty
    // input clears hits immediately (no debounce -- feels broken to
    // wait for empty-state to appear).
    useEffect(() => {
        if (debounceRef.current !== null) {
            window.clearTimeout(debounceRef.current);
            debounceRef.current = null;
        }
        if (!input.trim()) {
            setHits([]);
            setErr(null);
            return;
        }
        debounceRef.current = window.setTimeout(() => {
            void performSearch(input);
        }, DEBOUNCE_MS);
        return () => {
            if (debounceRef.current !== null) {
                window.clearTimeout(debounceRef.current);
                debounceRef.current = null;
            }
        };
    }, [input, performSearch]);
    // Outside click + Escape close the dropdown. Clicking inside the
    // wrapper (input or any hit) doesn't dismiss.
    useEffect(() => {
        if (!open)
            return;
        const onDocClick = (e) => {
            if (wrapRef.current && !wrapRef.current.contains(e.target)) {
                setOpen(false);
            }
        };
        const onKey = (e) => {
            if (e.key === 'Escape') {
                setOpen(false);
                setInput('');
            }
        };
        document.addEventListener('mousedown', onDocClick);
        document.addEventListener('keydown', onKey);
        return () => {
            document.removeEventListener('mousedown', onDocClick);
            document.removeEventListener('keydown', onKey);
        };
    }, [open]);
    // Extract documentId from a hit. The fdn_embeddings formation
    // stores the documentId in metadata; the embedding key itself is
    // chunk-specific so isn't useful for opening the source doc.
    const documentIdOf = useCallback((hit) => {
        const m = hit.metadata ?? {};
        if (typeof m.documentId === 'string')
            return m.documentId;
        return null;
    }, []);
    const handleHitClick = useCallback((hit) => {
        const docId = documentIdOf(hit);
        if (!docId)
            return;
        onOpenHit(docId, hit);
        setOpen(false);
        setInput('');
    }, [documentIdOf, onOpenHit]);
    // Group hits by documentId so multiple chunks from the same
    // document collapse into one row. Scoring: keep the highest-
    // scoring chunk's score for the row, and aggregate the chunks.
    const groupedHits = useMemo(() => {
        const groups = new Map();
        for (const hit of hits) {
            const docId = documentIdOf(hit);
            if (!docId)
                continue;
            const filename = hit.metadata?.filename
                ?? hit.metadata?.fdn_filename
                ?? docId.slice(0, 8);
            const score = hit.score ?? 0;
            const existing = groups.get(docId);
            if (existing) {
                existing.chunks.push(hit);
                if (score > existing.topScore)
                    existing.topScore = score;
            }
            else {
                groups.set(docId, {
                    docId,
                    filename,
                    topScore: score,
                    chunks: [hit],
                });
            }
        }
        return Array.from(groups.values()).sort((a, b) => b.topScore - a.topScore);
    }, [hits, documentIdOf]);
    const placeholder = atRoot ? 'Search project\u2026' : 'Search this folder\u2026';
    const showDropdown = open && (input.trim().length > 0 || err !== null);
    return (_jsxs("div", { className: "folder-search-wrap", ref: wrapRef, children: [_jsxs("div", { className: "folder-search-input-row", children: [_jsx("span", { className: "folder-search-glyph", "aria-hidden": true, children: '\u26B2' }), _jsx("input", { type: "text", className: "folder-search-input", placeholder: placeholder, value: input, onChange: (e) => { setInput(e.target.value); setOpen(true); }, onFocus: () => setOpen(true), "aria-label": atRoot ? 'Search project' : `Search folder ${scopeLabel}` }), input && (_jsx("button", { type: "button", className: "folder-search-clear", onClick: () => { setInput(''); setHits([]); setErr(null); }, title: "Clear", "aria-label": "Clear search", children: '\u2715' }))] }), input.trim() && (_jsxs("div", { className: "folder-search-scope", children: ["in: ", _jsx("span", { className: "folder-search-scope-label", children: scopeLabel })] })), showDropdown && (_jsxs("div", { className: "folder-search-dropdown", role: "listbox", children: [loading && (_jsx("div", { className: "folder-search-loading", children: 'Searching\u2026' })), err && !loading && (_jsx("div", { className: "folder-search-error", children: err })), !loading && !err && groupedHits.length === 0 && input.trim() && (_jsx("div", { className: "folder-search-empty", children: "No matches." })), !loading && !err && groupedHits.map((g) => (_jsxs("button", { type: "button", className: "folder-search-hit", onClick: () => handleHitClick(g.chunks[0]), role: "option", "aria-selected": false, children: [_jsxs("div", { className: "folder-search-hit-head", children: [_jsx("span", { className: "folder-search-hit-name", children: g.filename }), _jsxs("span", { className: "folder-search-hit-score", children: [(g.topScore * 100).toFixed(0), "%"] })] }), g.chunks.slice(0, 2).map((c, i) => (_jsx("div", { className: "folder-search-hit-snippet", children: (c.text ?? '').slice(0, 200) }, i))), g.chunks.length > 2 && (_jsxs("div", { className: "folder-search-hit-more", children: ["+", g.chunks.length - 2, " more excerpt", g.chunks.length - 2 === 1 ? '' : 's'] }))] }, g.docId)))] }))] }));
}
